import { useMemo } from 'react';
import type { MOQRulesLookup } from '~/components/molecules/ProductDetails/useProductMOQLimits';
import { convertApiRulesToLookup } from '~/utils/moqRulesApi.utils';
import { useWholesaleApiContext } from './useWholesaleApiContext';

const CACHE_DURATION_MS = 4 * 60 * 60 * 1000; // 4 hours

type UseMoqRulesLookupReturn = {
  rules: MOQRulesLookup;
  isLoading: boolean;
  error: Error | null;
  version: number | null;
};

/**
 * Hook to fetch and cache MOQ rules from the API
 * Falls back to empty Map if API fails
 * @returns MOQ rules lookup, loading state, and error
 */
export const useMoqRulesLookup = (): UseMoqRulesLookupReturn => {
  const { clientApi, bearerToken, accessToken } = useWholesaleApiContext();

  const { data, isLoading, error } = clientApi().getMoqRuleset.useQuery(
    ['moq-rules'],
    {
      headers: { authorization: bearerToken },
    },
    {
      enabled: Boolean(accessToken),
      staleTime: CACHE_DURATION_MS,
      cacheTime: CACHE_DURATION_MS * 2,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      retry: 2,
    },
  );

  const rules = useMemo(() => {
    if (data?.status === 200 && data.body.data.rules) {
      return convertApiRulesToLookup(data.body.data.rules);
    }
    return new Map();
  }, [data]);

  const version = data?.status === 200 ? data.body.data.version : null;

  return {
    rules,
    isLoading,
    error: error as Error | null,
    version,
  };
};
